import { useState } from 'react';
import { EntityModal } from '../common/EntityModal';

export type CeremonyPreset = 'grid' | 'curved' | 'vshape';

export interface CeremonyChairsOptions {
  preset: CeremonyPreset;
  rows: number;
  chairsPerRow: number;
  chairSpacingFt: number;
  rowSpacingFt: number;
  withAisle: boolean;
  aisleWidthFt: number;
}

export const DEFAULT_CEREMONY_OPTIONS: CeremonyChairsOptions = {
  preset: 'grid',
  rows: 10,
  chairsPerRow: 20,
  chairSpacingFt: 0.5,
  rowSpacingFt: 1.0,
  withAisle: true,
  aisleWidthFt: 4.0,
};

const PRESET_LABELS: Record<CeremonyPreset, string> = {
  grid: 'Grid',
  curved: 'Curved',
  vshape: 'V-Shape',
};

interface CeremonyChairsModalProps {
  open: boolean;
  onClose: () => void;
  onPlace: (options: CeremonyChairsOptions) => void;
}

const numberInput = 'w-full px-2 py-1 border border-gray-300 rounded text-sm';

/**
 * Rows of ceremony chairs as one object: grid, curved or V, with an optional
 * centre aisle. Keeps its settings between openings, so a second block is
 * placed the way the first was.
 */
export function CeremonyChairsModal({ open, onClose, onPlace }: CeremonyChairsModalProps) {
  const [opts, setOpts] = useState<CeremonyChairsOptions>(DEFAULT_CEREMONY_OPTIONS);
  const set = (patch: Partial<CeremonyChairsOptions>) => setOpts((o) => ({ ...o, ...patch }));

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="Place Ceremony Chairs"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onPlace(opts)}
            className="px-4 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700"
          >
            Place Chairs
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <div>
          <span className="block text-xs font-medium text-gray-600 mb-1">Preset</span>
          <div className="flex gap-2">
            {(Object.keys(PRESET_LABELS) as CeremonyPreset[]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => set({ preset: p })}
                aria-pressed={opts.preset === p}
                className={`flex-1 py-1.5 text-xs rounded border ${
                  opts.preset === p
                    ? 'bg-primary-100 border-primary-400 text-primary-700 font-medium'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                {PRESET_LABELS[p]}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs text-gray-500">
            Rows
            <input
              type="number"
              value={opts.rows}
              onChange={(e) => set({ rows: parseInt(e.target.value) || 1 })}
              min={1}
              max={30}
              className={numberInput}
            />
          </label>
          <label className="block text-xs text-gray-500">
            Chairs/Row
            <input
              type="number"
              value={opts.chairsPerRow}
              onChange={(e) => set({ chairsPerRow: parseInt(e.target.value) || 1 })}
              min={1}
              max={50}
              className={numberInput}
            />
          </label>
          <label className="block text-xs text-gray-500">
            Chair Spacing (ft)
            <input
              type="number"
              value={opts.chairSpacingFt}
              onChange={(e) => set({ chairSpacingFt: parseFloat(e.target.value) || 0.5 })}
              min={0}
              step={0.5}
              className={numberInput}
            />
          </label>
          <label className="block text-xs text-gray-500">
            Row Spacing (ft)
            <input
              type="number"
              value={opts.rowSpacingFt}
              onChange={(e) => set({ rowSpacingFt: parseFloat(e.target.value) || 0.5 })}
              min={0}
              step={0.5}
              className={numberInput}
            />
          </label>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="ceremony-aisle"
            checked={opts.withAisle}
            onChange={(e) => set({ withAisle: e.target.checked })}
            className="rounded"
          />
          <label htmlFor="ceremony-aisle" className="text-xs text-gray-600">
            Center aisle
          </label>
          {opts.withAisle && (
            <>
              <input
                type="number"
                value={opts.aisleWidthFt}
                onChange={(e) => set({ aisleWidthFt: parseFloat(e.target.value) || 2 })}
                min={2}
                step={0.5}
                className="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                title="Aisle width (ft)"
                aria-label="Aisle width (ft)"
              />
              <span className="text-xs text-gray-400">ft wide</span>
            </>
          )}
        </div>
        <div className="text-xs text-gray-400">~{opts.rows * opts.chairsPerRow} chairs total</div>
      </div>
    </EntityModal>
  );
}
